import { MediaContentSection as schema } from "#app/types/schema.ts"
import { SectionDefaultProps } from "#app/types/type.ts"
import { cn, resolveInternalLinkUrl } from "#app/utils/misc.tsx"
import { stegaClean } from "@sanity/client/stega"
import SanityImage from "./sanity-image"
import SanityLink from "./sanity-link"
import { PortableText, PortableTextComponents } from "@portabletext/react"
import { Link } from "react-router"
import { Heading } from "./text"

type MediaContentSectionProps = SectionDefaultProps & {
    data: schema
}

const portableTextComponents: PortableTextComponents = {
    marks: {
        link: ({ children, value }) => {
            const href = value?.href || '';
            const isExternal = href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');
            const openInNewTab = value?.blank === true; // Check the 'blank' property fetched via GROQ

            return (
                <Link 
                to={href} 
                className="text-primary hover:underline" 
                prefetch={isExternal ? 'intent' : undefined}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                >
                    {children}
                </Link>
            );
        },
        internalLink: ({ children, value }) => {
            // 'value' now contains { _key, _type: 'internalLink', reference: {...}, slug: '...', docType: '...' }
            // thanks to the GROQ query modification.
            const urlPath = resolveInternalLinkUrl(value?.docType, value?.slug);

            if (!urlPath) {
                // Fallback for missing link data or unhandled type
                return <span className="text-red-500 italic">(Invalid Link) {children}</span>;
            }

            return (
                <Link to={urlPath} className="text-primary hover:underline" prefetch="intent">
                    {children}
                </Link>
            );
        },
        customPathLink: ({ children, value }) => {
            const path = value?.path;
    
            if (!path) {
              // Fallback if path is missing
              return <span className="text-red-500 italic">(Invalid Custom Link Path) {children}</span>;
            }
    
            // Use React Router Link with the predefined path
            return (
              <Link to={path} className="text-primary hover:underline" prefetch="intent">
                {children}
              </Link>
            );
        },
    },
};

export default function MediaContentSection({ data, encodeDataAttribute }: MediaContentSectionProps) {
    const mediaContent = stegaClean(data);
    const { tagline, heading, headingLevel, content, buttonGroup, media, mediaPosition, layoutWidth, backgroundStyle } = mediaContent
    const contentClasses = cn(
        "lg:px-0",
        mediaPosition === "right" ? "lg:pr-4 lg:pt-4" : "lg:pl-4 lg:pt-4 lg:order-2",
    )
	return (
		<div className="relative isolate overflow-hidden">
			<div className="container">
				<div className="grid grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:grid-cols-2 lg:items-start">
					<div className={contentClasses}>
						<div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-lg">
							{tagline && (
                                <p className="text-base/7 text-accent-foreground font-bold font-serif">{tagline}</p>
                            )}
                            <Heading as={headingLevel} className="mt-2 text-pretty font-semibold tracking-tight text-h1">
                                {heading}
                            </Heading>
							{content && (
                                <div className="prose dark:prose-invert mt-6 text-lg/8">
                                    <PortableText value={content} components={portableTextComponents} />
                                </div>
                            )}
                            {buttonGroup && buttonGroup.length > 0 && (
                                <div className='mt-10 flex flex-wrap gap-5'>
                                    {buttonGroup.map((button, index) => (
                                        <div key={index} className="flex-shrink-0">
                                            <SanityLink link={button} />
                                        </div>
                                    ))}
                                </div>
                            )}
						</div>
					</div>
					<div className="sm:px-6 lg:px-0">
						<div className="aspect-square sm:px-6 lg:px-0">
                            <SanityImage
                                image={media}
                                alt={media.alt ?? ''}
                                className="object-cover w-full h-full rounded-md"
                            />
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}